import React, { useState, useEffect } from "react";
import { 
  Calendar, 
  Clock, 
  Upload, 
  X, 
  CheckCircle, 
  Sparkles, 
  User, 
  FileText, 
  BookOpen, 
  TrendingUp,
  Info,
  GraduationCap,
  Send,
  ChevronRight,
  ChevronLeft,
  AlertCircle,
  Star,
  RefreshCw,
  ArrowLeft,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function BookingForm() {
  const [formData, setFormData] = useState({
    parentFirstName: "",
    parentLastName: "",
    parentEmail: "",
    parentPhone: "",
    studentFirstName: "",
    studentLastName: "",
    studentEmail: "",
    studentPhone: "",
    gradeLevel: "",
    subjects: [],
    selectedTopic: "",
    goals: "",
    additionalNotes: ""
  });

  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});
  const [darkMode, setDarkMode] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [tutors, setTutors] = useState([]);
  const [filteredTutors, setFilteredTutors] = useState([]);
  const [tutorsLoading, setTutorsLoading] = useState(true);
  const [tutorsError, setTutorsError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentCalendarYear, setCurrentCalendarYear] = useState(new Date().getFullYear());
  const [currentCalendarMonth, setCurrentCalendarMonth] = useState(new Date().getMonth());
  const [availableSlotsByDate, setAvailableSlotsByDate] = useState({});
  const [selectedDate, setSelectedDate] = useState(null);
  const [calendarLoading, setCalendarLoading] = useState(false);
  const navigate = useNavigate();
  const [bookedSessions, setBookedSessions] = useState([]);
  const [showCalendar, setShowCalendar] = useState(false);
  const [selectedTutorForCalendar, setSelectedTutorForCalendar] = useState(null);
  const [selectedTopicForCalendar, setSelectedTopicForCalendar] = useState("");
  const [addingNewSession, setAddingNewSession] = useState(false);
  const [showTutorSelection, setShowTutorSelection] = useState(false);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone) => {
    const cleaned = phone.replace(/\D/g, '');
    return cleaned.length >= 10 && cleaned.length <= 15;
  };

  const formatPhoneNumber = (value) => {
    const cleaned = value.replace(/\D/g, '');
    
    if (cleaned.length <= 3) {
      return cleaned;
    } else if (cleaned.length <= 6) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
    } else {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
    }
  };

  const subjects = ["Math", "English", "History", "Biology", "Chemistry", "Physics", "Computer Science"];
  const gradeLevels = ["5th Grade", "6th Grade", "7th Grade", "8th Grade", "9th Grade", "10th Grade", "11th Grade", "12th Grade"];

  const defaultTutorImages = {
    "Math": "https://images.unsplash.com/photo-1635070041078-e363dbe005cb?w=400&h=400&fit=crop&crop=face",
    "English": "https://images.unsplash.com/photo-1568602471122-7832951cc4c5?w=400&h=400&fit=crop&crop=face",
    "Science": "https://images.unsplash.com/photo-1582750433449-648ed127bb54?w=400&h=400&fit=crop&crop=face",
    "History": "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=400&fit=crop&crop=face",
    "Computer Science": "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=400&h=400&fit=crop&crop=face",
    "default": "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=400&h=400&fit=crop&crop=face"
  };

  // Calculate total price based on number of sessions
  const calculatePrice = () => {
    const sessionCount = bookedSessions.length;
    if (sessionCount === 0) return 0;
    
    // Pricing structure:
    // 1 -> 65
    // 2 -> 130
    // 3 -> 165
    // 4 -> 230
    // 5 -> 305
    // 6 -> 330
    // Pattern continues...
    
    if (sessionCount === 1) return 65;
    if (sessionCount === 2) return 130;
    if (sessionCount === 3) return 165;
    if (sessionCount === 4) return 230;
    if (sessionCount === 5) return 305;
    if (sessionCount === 6) return 330;
    
    // For more than 6 sessions, follow the pattern
    let basePrice = 0;
    const remainingSessions = sessionCount;
    
    // Apply bulk discounts
    const groupsOfThree = Math.floor(remainingSessions / 3);
    const remainder = remainingSessions % 3;
    
    basePrice = groupsOfThree * 165;
    
    if (remainder === 1) basePrice += 65;
    if (remainder === 2) basePrice += 130;
    
    return basePrice;
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    
    const checkDarkMode = () => {
      const isDark = document.documentElement.classList.contains('dark');
      setDarkMode(isDark);
    };
    
    checkDarkMode();
    
    const observer = new MutationObserver(checkDarkMode);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    });
    
    fetchTutors();
    
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentStep]);

  useEffect(() => {
    if (selectedTutorForCalendar) {
      calculateAvailableSlots(selectedTutorForCalendar);
    }
  }, [selectedTutorForCalendar, currentCalendarYear, currentCalendarMonth]);

  useEffect(() => {
    if (formData.selectedTopic && tutors.length > 0) {
      const filtered = tutors.filter(tutor => 
        tutor.subjects && 
        tutor.subjects.some(subject => 
          subject.toLowerCase().includes(formData.selectedTopic.toLowerCase())
        )
      );
      setFilteredTutors(filtered);
    } else {
      setFilteredTutors(tutors);
    }
  }, [formData.selectedTopic, tutors]);

  useEffect(() => {
    if (showCalendar) {
      setTimeout(() => {
        const scheduleSection = document.getElementById('schedule-section');
        if (scheduleSection) {
          scheduleSection.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'center'
          });
        }
      }, 10);
    }
  }, [showCalendar]);

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    return { daysInMonth, startingDayOfWeek, year, month };
  };

  const formatDateKey = (year, month, day) => {
    return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  };

  const isWithinBookingWindow = (date) => {
    const now = new Date();
    const minBookingDate = new Date(now.getTime() + 24 * 60 * 60 * 1000);
    const maxBookingDate = new Date(now.getTime() + 90 * 24 * 60 * 60 * 1000);
    
    return date >= minBookingDate && date <= maxBookingDate;
  };

  const calculateAvailableSlots = (tutor) => {
    if (!tutor || !tutor.availability) {
      setAvailableSlotsByDate({});
      return;
    }
    
    setCalendarLoading(true);
    
    const slots = {};
    const { year, month } = getDaysInMonth(new Date(currentCalendarYear, currentCalendarMonth, 1));
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    
    const dayNameToNumber = {
      'Sunday': 0,
      'Monday': 1,
      'Tuesday': 2,
      'Wednesday': 3,
      'Thursday': 4,
      'Friday': 5,
      'Saturday': 6
    };
    
    const tutorAvailability = tutor.availability;
    
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      
      if (isWithinBookingWindow(date)) {
        const dayOfWeek = date.getDay();
        const dayName = Object.keys(dayNameToNumber).find(
          name => dayNameToNumber[name] === dayOfWeek
        );
        
        if (dayName && tutorAvailability[dayName]) {
          const dateKey = formatDateKey(year, month, day);
          
          if (Array.isArray(tutorAvailability[dayName]) && tutorAvailability[dayName].length > 0) {
            slots[dateKey] = tutorAvailability[dayName];
          } 
          else if (tutorAvailability[dayName] && tutorAvailability[dayName].timeSlots && Array.isArray(tutorAvailability[dayName].timeSlots)) {
            slots[dateKey] = tutorAvailability[dayName].timeSlots;
          }
          else if (typeof tutorAvailability[dayName] === 'string') {
            const timeSlots = tutorAvailability[dayName].split(',').map(slot => slot.trim());
            if (timeSlots.length > 0) {
              slots[dateKey] = timeSlots;
            }
          }
        }
      }
    }
    
    setAvailableSlotsByDate(slots);
    setCalendarLoading(false);
  };

  const fetchTutors = async () => {
    setTutorsLoading(true);
    setTutorsError("");
    try {
      const response = await fetch(`https://api.zenithacademics.com/api/tutors/approved`);
      if (!response.ok) {
        throw new Error(`Failed to fetch tutors: ${response.status}`);
      }
      const data = await response.json();
      
      if (data.success) {
        const tutorsWithImages = data.data.map(tutor => ({
          ...tutor,
          photo_url: tutor.photo_url || getDefaultTutorImage(tutor.subjects?.[0])
        }));
        setTutors(tutorsWithImages);
        setFilteredTutors(tutorsWithImages);
      } else {
        setTutorsError(data.message || "Failed to load tutors");
      }
    } catch (error) {
      console.error("Error fetching tutors:", error);
      setTutorsError("Unable to load tutors. Please try again later.");
    } finally {
      setTutorsLoading(false);
    }
  };

  const getDefaultTutorImage = (subject) => {
    if (subject) {
      const lowerSubject = subject.toLowerCase();
      if (lowerSubject.includes("math")) return defaultTutorImages["Math"];
      if (lowerSubject.includes("english")) return defaultTutorImages["English"];
      if (lowerSubject.includes("biology") || lowerSubject.includes("chemistry") || lowerSubject.includes("physics")) 
        return defaultTutorImages["Science"];
      if (lowerSubject.includes("history")) return defaultTutorImages["History"];
      if (lowerSubject.includes("computer")) return defaultTutorImages["Computer Science"];
    }
    return defaultTutorImages["default"];
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    let newValue = value;
    
    if (name === 'parentPhone' || name === 'studentPhone') {
      newValue = formatPhoneNumber(value);
    }
    
    setFormData(prev => ({ ...prev, [name]: newValue }));
    
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: "" }));
    }
    
    if ((name === 'parentEmail' || name === 'studentEmail') && value.trim()) {
      if (!validateEmail(value.trim())) {
        setErrors(prev => ({ 
          ...prev, 
          [name]: "Please enter a valid email address" 
        }));
      }
    }
    
    if ((name === 'parentPhone' || name === 'studentPhone') && value.trim()) {
      if (!validatePhone(value.replace(/\D/g, ''))) {
        setErrors(prev => ({ 
          ...prev, 
          [name]: "Please enter a valid phone number (10-15 digits)" 
        }));
      }
    }
  };

  const handleTopicSelect = (topic) => {
    setFormData(prev => ({ 
      ...prev, 
      selectedTopic: topic,
      subjects: [topic]
    }));
    setAddingNewSession(false);
  };

  const openCalendarForTutor = (tutor, topic) => {
    setSelectedTutorForCalendar(tutor);
    setSelectedTopicForCalendar(topic || formData.selectedTopic);
    setShowCalendar(true);
    setSelectedDate(null);
  };

  const handleDateClick = (day) => {
    if (!day || day.isPast || !day.isAvailable || !day.isWithinBookingWindow) return;
    setSelectedDate(day.formattedDate);
  };

  const handleTimeSlotSelect = (timeSlot) => {
    if (!selectedTutorForCalendar || !selectedDate) return;
    
    const newSession = {
      tutorId: selectedTutorForCalendar.id,
      tutorName: selectedTutorForCalendar.full_name || selectedTutorForCalendar.name,
      tutorImage: selectedTutorForCalendar.photo_url,
      date: selectedDate,
      time: timeSlot,
      subject: selectedTopicForCalendar || formData.selectedTopic,
      key: Date.now() + Math.random()
    };
    
    setBookedSessions(prev => [...prev, newSession]);
    setShowCalendar(false);
    setSelectedDate(null);
    setSelectedTutorForCalendar(null);
    setSelectedTopicForCalendar("");
    setFormData(prev => ({ 
      ...prev, 
      selectedTopic: "",
      subjects: [...prev.subjects, selectedTopicForCalendar || formData.selectedTopic].filter((value, index, self) => self.indexOf(value) === index)
    }));
    setAddingNewSession(false);
  };

  const handleFileUpload = (e) => {
    const newFiles = Array.from(e.target.files);
    setFiles(prev => [...prev, ...newFiles]);
    if (errors.files) {
      setErrors(prev => ({ ...prev, files: "" }));
    }
  };

  const handleAddNewSession = () => {
    setFormData(prev => ({ ...prev, selectedTopic: "" }));
    setAddingNewSession(true);
    setShowTutorSelection(false);
    
    setTimeout(() => {
      const topicSection = document.getElementById('topic-section');
      if (topicSection) {
        topicSection.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }
    }, 100);
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const removeBookedSession = (index) => {
    const sessionToRemove = bookedSessions[index];
    setBookedSessions(prev => prev.filter((_, i) => i !== index));
    const remainingSubjects = bookedSessions
      .filter((_, i) => i !== index)
      .map(s => s.subject);
    
    setFormData(prev => ({
      ...prev,
      subjects: [...new Set(remainingSubjects)]
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.parentFirstName.trim()) newErrors.parentFirstName = "Parent first name is required";
    if (!formData.parentLastName.trim()) newErrors.parentLastName = "Parent last name is required";
    if (!formData.parentEmail.trim()) {
      newErrors.parentEmail = "Parent email is required";
    } else if (!validateEmail(formData.parentEmail.trim())) {
      newErrors.parentEmail = "Please enter a valid email address";
    }
    if (!formData.parentPhone.trim()) {
      newErrors.parentPhone = "Parent phone number is required";
    } else if (!validatePhone(formData.parentPhone)) {
      newErrors.parentPhone = "Please enter a valid phone number (10-15 digits)";
    }
    
    if (!formData.studentFirstName.trim()) newErrors.studentFirstName = "Student first name is required";
    if (!formData.studentLastName.trim()) newErrors.studentLastName = "Student last name is required";
    if (!formData.studentEmail.trim()) newErrors.studentEmail = "Student email is required";
    if (!formData.gradeLevel) newErrors.gradeLevel = "Grade level is required";
    if (!formData.studentEmail.trim()) {
      newErrors.studentEmail = "Student email is required";
    } else if (!validateEmail(formData.studentEmail.trim())) {
      newErrors.studentEmail = "Please enter a valid email address";
    }
    if (formData.studentPhone.trim() && !validatePhone(formData.studentPhone)) {
      newErrors.studentPhone = "Please enter a valid phone number (10-15 digits)";
    }

    if (bookedSessions.length === 0) newErrors.bookedSessions = "Please book at least one session";
    if (files.length === 0) newErrors.files = "At least one study material is required";
    if (!formData.goals.trim()) newErrors.goals = "Learning goals are required";
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const formatTimeForDisplay = (timeString) => {
    if (!timeString) return "";
    if (timeString.includes('AM') || timeString.includes('PM')) {
      return timeString;
    }
    const [hours, minutes] = timeString.split(':').map(Number);
    if (isNaN(hours) || isNaN(minutes)) return timeString;
    
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    return `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}`;
  };

  const nextStep = () => {
    let isValid = true;
    const newErrors = {};
    
    if (currentStep === 1) {
      if (!formData.parentFirstName.trim()) newErrors.parentFirstName = "Parent first name is required";
      if (!formData.parentLastName.trim()) newErrors.parentLastName = "Parent last name is required";
      if (!formData.parentEmail.trim()) {
        newErrors.parentEmail = "Parent email is required";
      } else if (!validateEmail(formData.parentEmail.trim())) {
        newErrors.parentEmail = "Please enter a valid email address";
      }
      if (!formData.parentPhone.trim()) {
        newErrors.parentPhone = "Parent phone number is required";
      } else if (!validatePhone(formData.parentPhone)) {
        newErrors.parentPhone = "Please enter a valid phone number (10-15 digits)";
      }
      if (!formData.studentFirstName.trim()) newErrors.studentFirstName = "Student first name is required";
      if (!formData.studentLastName.trim()) newErrors.studentLastName = "Student last name is required";
      if (!formData.studentEmail.trim()) {
        newErrors.studentEmail = "Student email is required";
      } else if (!validateEmail(formData.studentEmail.trim())) {
        newErrors.studentEmail = "Please enter a valid email address";
      }
      if (formData.studentPhone.trim() && !validatePhone(formData.studentPhone)) {
        newErrors.studentPhone = "Please enter a valid phone number (10-15 digits)";
      }
      if (!formData.gradeLevel) newErrors.gradeLevel = "Grade level is required";
    } else if (currentStep === 2) {
      if (bookedSessions.length === 0) newErrors.bookedSessions = "Please book at least one session";
    } else if (currentStep === 3) {
      if (files.length === 0) newErrors.files = "At least one study material is required";
      if (!formData.goals.trim()) newErrors.goals = "Learning goals are required";
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      isValid = false;
    }
    
    if (isValid) {
      setCurrentStep(prev => Math.min(prev + 1, 3));
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

const handleSubmit = async (e) => {
  e.preventDefault();
  
  if (isSubmitting) return;
  
  if (!validateForm()) {
    const firstError = Object.keys(errors)[0];
    if (firstError) {
      const errorElement = document.querySelector(`[data-error="${firstError}"]`) || 
                         document.querySelector(`[name="${firstError}"]`);
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
    return;
  }

  setIsSubmitting(true);
  setLoading(true);
  
  try {
    // Prepare files for upload
    let uploadedFiles = [];
    if (files.length > 0) {
      uploadedFiles = files.map(file => ({
        name: file.name,
        size: file.size,
        type: file.type,
        url: URL.createObjectURL(file)
      }));
    }

    // Prepare time slots in correct format
    const formattedTimeSlots = bookedSessions.map(session => ({
      tutorId: session.tutorId,
      tutorName: session.tutorName,
      date: session.date,
      time: session.time,
      subject: session.subject,
      sessionId: session.key
    }));

    // **FIX: Get subjects from bookedSessions and ensure it's a string**
    const uniqueSubjects = Array.from(new Set(bookedSessions.map(s => s.subject)));
    const subjectsString = uniqueSubjects.join(', '); // Make sure it's a string
    
    console.log('📋 Subjects from bookedSessions:', uniqueSubjects);
    console.log('📋 Subjects string:', subjectsString);

    // Prepare booking data EXACTLY matching database expectations
    const bookingData = {
      // Database field: student_name
      studentName: `${formData.studentFirstName.trim()} ${formData.studentLastName.trim()}`,
      
      // Database field: student_email
      studentEmail: formData.studentEmail.trim(),
      
      // Database field: student_phone (optional)
      studentPhone: formData.studentPhone ? formData.studentPhone.replace(/\D/g, '') : '',
      
      // Database field: grade_level
      gradeLevel: formData.gradeLevel,
      
      // Database field: parent_name
      parentName: `${formData.parentFirstName.trim()} ${formData.parentLastName.trim()}`,
      
      // Database field: parent_email
      parentEmail: formData.parentEmail.trim(),
      
      // Database field: parent_phone
      parentPhone: formData.parentPhone.replace(/\D/g, ''),
      
      // **FIX: Database field: subjects (MUST be a string, not array)**
      subjects: subjectsString,
      
      // Database field: tutor_id
      tutorId: bookedSessions.length > 0 ? bookedSessions[0].tutorId : '',
      
      // Database field: tutor_name
      tutorName: bookedSessions.length > 0 ? bookedSessions[0].tutorName : '',
      
      // Database field: selected_time_slots (array)
      timeSlots: formattedTimeSlots,
      
      // Database field: goals
      goals: formData.goals.trim(),
      
      // Database field: additional_notes
      additionalNotes: formData.additionalNotes.trim(),
      
      // Database field: files (array)
      studyMaterials: uploadedFiles,
      
      // Database field: total_price
      totalAmount: calculatePrice()
    };

    console.log('📤 Submitting booking data:', JSON.stringify(bookingData, null, 2));
    console.log('📊 Validation check:');
    console.log('- Student Name:', bookingData.studentName);
    console.log('- Subjects (type):', typeof bookingData.subjects, 'value:', bookingData.subjects);
    console.log('- Subjects can trim?', typeof bookingData.subjects === 'string');
    console.log('- Tutor ID:', bookingData.tutorId);
    console.log('- Tutor Name:', bookingData.tutorName);
    console.log('- TimeSlots count:', bookingData.timeSlots.length);
    console.log('- Total Amount:', bookingData.totalAmount);

    // **FIX: Enhanced validation with better error messages**
    const validationErrors = [];
    
    if (!bookingData.studentName || bookingData.studentName.trim() === '') 
      validationErrors.push('Student name is required');
    
    if (!bookingData.studentEmail || bookingData.studentEmail.trim() === '') 
      validationErrors.push('Student email is required');
    
    if (!bookingData.gradeLevel || bookingData.gradeLevel.trim() === '') 
      validationErrors.push('Grade level is required');
    
    if (!bookingData.parentName || bookingData.parentName.trim() === '') 
      validationErrors.push('Parent name is required');
    
    if (!bookingData.parentEmail || bookingData.parentEmail.trim() === '') 
      validationErrors.push('Parent email is required');
    
    if (!bookingData.parentPhone || bookingData.parentPhone.trim() === '') 
      validationErrors.push('Parent phone is required');
    
    // **FIX: Check if subjects is a string and not empty**
    if (!bookingData.subjects || typeof bookingData.subjects !== 'string') {
      validationErrors.push('Subjects must be a string');
    } else if (bookingData.subjects.trim() === '') {
      validationErrors.push('Subjects are required');
    }
    
    if (!bookingData.tutorId || bookingData.tutorId === '') 
      validationErrors.push('Tutor selection is required');
    
    if (!bookingData.tutorName || bookingData.tutorName.trim() === '') 
      validationErrors.push('Tutor name is required');
    
    if (!bookingData.timeSlots || !Array.isArray(bookingData.timeSlots) || bookingData.timeSlots.length === 0) 
      validationErrors.push('At least one time slot is required');
    
    if (!bookingData.totalAmount || bookingData.totalAmount <= 0) 
      validationErrors.push('Valid total price is required');
    
    if (!bookingData.goals || bookingData.goals.trim() === '') 
      validationErrors.push('Learning goals are required');
    
    if (validationErrors.length > 0) {
      throw new Error(`Validation errors:\n${validationErrors.join('\n')}`);
    }

    const response = await fetch(`https://api.zenithacademics.com/api/bookings`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(bookingData),
    });

    const data = await response.json();
    console.log('📥 API Response:', data);

    if (!response.ok) {
      if (data.errors && Array.isArray(data.errors)) {
        const errorMessages = data.errors.join('\n');
        throw new Error(`Validation errors:\n${errorMessages}`);
      }
      throw new Error(data.message || data.error || `Server error: ${response.status}`);
    }

    if (!data.success) {
      throw new Error(data.message || data.error || 'Booking submission failed');
    }

    setSubmitted(true);
  } catch (error) {
    console.error("❌ Error submitting booking:", error);
    if (error.message.includes('Validation errors:')) {
      alert(`Please fix the following errors:\n\n${error.message.split('Validation errors:')[1]}`);
    } else {
      alert(`Failed to submit booking: ${error.message}\n\nPlease check that all required fields are filled correctly and try again.`);
    }
  } finally {
    setLoading(false);
    setIsSubmitting(false);
  }
};

  const navigateCalendar = (direction) => {
    const newDate = new Date(currentCalendarYear, currentCalendarMonth + direction, 1);
    const now = new Date();
    const currentDate = new Date(currentCalendarYear, currentCalendarMonth, 1);
    const maxDate = new Date(now.getTime() + 90 * 24 * 60 * 60 * 1000);
    
    if (newDate <= maxDate) {
      if (newDate.getFullYear() < now.getFullYear() || 
          (newDate.getFullYear() === now.getFullYear() && newDate.getMonth() < now.getMonth())) {
        return;
      }
      
      setCurrentCalendarYear(newDate.getFullYear());
      setCurrentCalendarMonth(newDate.getMonth());
      setSelectedDate(null);
    }
  };

  const getMonthName = (year, month) => {
    const date = new Date(year, month, 1);
    return date.toLocaleString('default', { month: 'long', year: 'numeric' });
  };

  const renderCalendar = () => {
    const {
      daysInMonth,
      startingDayOfWeek,
      year,
      month,
    } = getDaysInMonth(
      new Date(currentCalendarYear, currentCalendarMonth, 1)
    );

    const days = [];
    const today = new Date();

    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(<div key={`empty-${i}`} className="h-12"></div>);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const dateKey = formatDateKey(year, month, day);
      const dateObj = new Date(year, month, day);

      const isAvailable =
        availableSlotsByDate[dateKey] &&
        availableSlotsByDate[dateKey].length > 0;

      const isWithinWindow = isWithinBookingWindow(dateObj);
      const isPast = !isWithinWindow && dateObj < new Date();
      const isSelected = selectedDate === dateKey;
      const isToday = dateObj.toDateString() === today.toDateString();

      days.push(
        <button
          key={day}
          type="button"
          onClick={() =>
            handleDateClick({
              formattedDate: dateKey,
              isPast,
              isAvailable,
              isWithinBookingWindow: isWithinWindow,
              dayOfMonth: day,
            })
          }
          disabled={!isAvailable || !isWithinWindow}
          className={`h-12 rounded-xl font-semibold transition-all ${
            isSelected
              ? darkMode
                ? 'bg-gradient-to-r from-red-500 to-orange-500 text-white shadow-lg scale-110'
                : 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg scale-110'
              : isAvailable && isWithinWindow
              ? darkMode
                ? 'bg-gradient-to-r from-green-900 to-emerald-900 text-green-300 border-2 border-green-700 hover:scale-105'
                : 'bg-gradient-to-r from-green-100 to-emerald-100 text-green-700 border-2 border-green-300 hover:bg-green-200 hover:scale-105'
              : darkMode
                ? 'bg-zinc-800 text-zinc-500 cursor-not-allowed'
                : 'bg-gray-50 text-gray-300 cursor-not-allowed'
          } ${isToday ? 'ring-2 ring-blue-500 dark:ring-red-500' : ''}`}
        >
          <div className="flex flex-col items-center">
            <span>{day}</span>
            {isToday && (
              <span className="text-xs font-normal text-blue-600 dark:text-red-400">
                Today
              </span>
            )}
          </div>
        </button>
      );
    }

    return days;
  };

  if (tutorsLoading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${darkMode ? 'bg-gradient-to-b from-zinc-950 via-zinc-900 to-zinc-950' : 'bg-gradient-to-br from-slate-50 via-blue-50 to-purple-50'}`}>
        <div className="text-center">
          <div className="inline-block animate-spin">
            <RefreshCw className="w-16 h-16 text-purple-600 dark:text-red-400" />
          </div>
          <p className={`mt-4 text-lg ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Loading tutors...</p>
        </div>
      </div>
    );
  }

  if (tutorsError && tutors.length === 0) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${darkMode ? 'bg-gradient-to-b from-zinc-950 via-zinc-900 to-zinc-950' : 'bg-gradient-to-br from-slate-50 via-blue-50 to-purple-50'}`}>
        <div className={`max-w-md p-8 rounded-2xl ${darkMode ? 'bg-zinc-900/90' : 'bg-white/90'} backdrop-blur-sm shadow-2xl`}>
          <AlertCircle className={`w-16 h-16 mx-auto mb-4 ${darkMode ? 'text-red-400' : 'text-red-500'}`} />
          <h2 className={`text-2xl font-bold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Unable to Load Tutors</h2>
          <p className={`mb-6 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>{tutorsError}</p>
          <div className="flex gap-4">
            <button
              onClick={fetchTutors}
              className={`flex-1 ${darkMode ? 'bg-red-500 hover:bg-red-600' : 'bg-blue-500 hover:bg-blue-600'} text-white py-3 rounded-xl font-bold`}
            >
              Try Again
            </button>
            <button
              onClick={() => navigate("/")}
              className={`flex-1 ${darkMode ? 'bg-zinc-800 hover:bg-zinc-700' : 'bg-gray-500 hover:bg-gray-600'} text-white py-3 rounded-xl font-bold`}
            >
              Return to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className={`min-h-screen py-12 md:py-20 ${darkMode ? 'bg-gradient-to-b from-zinc-950 via-zinc-900 to-zinc-950' : 'bg-gradient-to-br from-slate-50 via-blue-50 to-purple-50'} flex items-center justify-center px-4`}>
        <div className="max-w-2xl w-full animate-fadeIn">
          <div className={`p-12 text-center ${darkMode ? 'bg-zinc-900/90 border-green-500/40' : 'bg-white border-green-200'} rounded-2xl border-3 shadow-2xl`}>
            <div className={`w-24 h-24 ${darkMode ? 'bg-gradient-to-br from-green-500 to-emerald-600' : 'bg-gradient-to-br from-green-400 to-emerald-500'} rounded-full flex items-center justify-center mx-auto mb-6 animate-scaleIn`}>
              <CheckCircle className="w-12 h-12 text-white" />
            </div>
            <h2 className={`text-4xl font-black ${darkMode ? 'text-white' : 'text-gray-900'} mb-4`}>Booking Submitted!</h2>
            <p className={`text-xl ${darkMode ? 'text-zinc-300' : 'text-gray-600'} mb-8`}>
              Thank you for booking with us. We'll review your request and get back to you within 24 hours to confirm your session details.
            </p>
            <button
              onClick={() => navigate("/")}
              className={`${darkMode ? 'bg-gradient-to-r from-red-500 to-orange-600' : 'bg-gradient-to-r from-blue-600 to-purple-600'} text-white font-bold px-8 py-4 rounded-xl hover:opacity-90 transition-all shadow-lg`}
            >
              Return to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen py-12 md:py-20 ${darkMode ? 'bg-gradient-to-b from-zinc-950 via-zinc-900 to-zinc-950' : 'bg-gradient-to-br from-slate-50 via-blue-50 to-purple-50'} relative overflow-hidden transition-colors duration-300`}>
      <div className="absolute inset-0 opacity-20 overflow-hidden">
        <div className={`absolute top-20 left-10 w-96 h-96 ${darkMode ? 'bg-red-600/30' : 'bg-blue-300'} rounded-full blur-3xl animate-float`}></div>
        <div className={`absolute top-1/3 right-10 w-80 h-80 ${darkMode ? 'bg-orange-500/30' : 'bg-purple-300'} rounded-full blur-3xl animate-float`} style={{animationDelay: '2s'}}></div>
        <div className={`absolute bottom-20 left-1/4 w-96 h-96 ${darkMode ? 'bg-red-500/20' : 'bg-pink-300'} rounded-full blur-3xl animate-float`} style={{animationDelay: '4s'}}></div>
        <div className={`absolute top-1/2 left-1/2 w-72 h-72 ${darkMode ? 'bg-amber-500/20' : 'bg-cyan-300'} rounded-full blur-3xl animate-float`} style={{animationDelay: '3s'}}></div>
      </div>

      {/* Return to Home Button */}
      <div className="fixed top-6 left-6 z-50">
        <button
          onClick={() => navigate("/")}
          className={`flex items-center gap-2 px-5 py-3 rounded-xl ${darkMode ? 'bg-zinc-800/80 backdrop-blur-sm hover:bg-zinc-700/80 text-white border-2 border-zinc-700' : 'bg-white/80 backdrop-blur-sm hover:bg-gray-100 text-gray-700 border-2 border-gray-200'} shadow-lg hover:scale-105 transition-all duration-300`}
        >
          <ArrowLeft className="w-5 h-5" />
          <span className="font-semibold">Return to Home</span>
        </button>
      </div>

      <div className="fixed top-6 right-6 z-50">
        <button
          onClick={() => setDarkMode(!darkMode)}
          className={`p-4 rounded-2xl ${darkMode ? 'bg-zinc-800/80 backdrop-blur-sm text-yellow-400 shadow-[0_0_20px_rgba(251,191,36,0.3)]' : 'bg-white/80 backdrop-blur-sm text-gray-700 shadow-lg'} hover:scale-110 transition-all duration-300 border-2 ${darkMode ? 'border-zinc-700' : 'border-gray-200'}`}
        >
          <span className="text-2xl">{darkMode ? '☀️' : '🌙'}</span>
        </button>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="text-center mb-16 animate-fadeIn">
          <h1 className={`text-6xl md:text-8xl font-extrabold ${darkMode ? 'bg-gradient-to-r from-white via-red-400 to-orange-400' : 'bg-gradient-to-r from-green-900 via-blue-900 to-purple-900'} bg-clip-text text-transparent mb-8 pb-2 leading-tight tracking-tight`}>
            Schedule Tutoring
          </h1>
          <p className={`text-xl md:text-2xl ${darkMode ? 'text-zinc-300' : 'text-gray-600'} max-w-3xl mx-auto leading-relaxed`}>
            Get personalized 1-on-1 tutoring from our <span className={`font-bold ${darkMode ? 'text-red-400' : 'text-blue-600'}`}>expert tutors</span> and achieve academic excellence
          </p>
        </div>

        <div className="mb-16">
          <div className="flex items-center justify-between max-w-3xl mx-auto relative">
            <div className={`absolute top-6 left-0 right-0 h-1 ${darkMode ? 'bg-zinc-800' : 'bg-gray-200'} rounded-full`}>
              <div 
                className={`h-full ${darkMode ? 'bg-gradient-to-r from-red-500 to-orange-500' : 'bg-gradient-to-r from-blue-500 to-purple-500'} rounded-full transition-all duration-500`}
                style={{ width: `${((currentStep - 1) / 2) * 100}%` }}
              />
            </div>

            {[1, 2, 3].map((step) => (
              <div key={step} className="flex flex-col items-center relative z-10">
                <div className={`w-14 h-14 rounded-full flex items-center justify-center mb-3 font-bold text-lg transition-all duration-300 ${currentStep >= step 
                  ? `${darkMode ? 'bg-gradient-to-br from-red-500 to-orange-500 shadow-[0_0_20px_rgba(239,68,68,0.5)]' : 'bg-gradient-to-br from-blue-500 to-purple-500 shadow-lg'} text-white scale-110` 
                  : `${darkMode ? 'bg-zinc-800 border-2 border-zinc-700' : 'bg-white border-2 border-gray-300 shadow-md'} text-gray-500 dark:text-zinc-500`}`}>
                  {currentStep > step ? <CheckCircle className="w-6 h-6" /> : step}
                </div>
                <span className={`text-sm font-semibold transition-colors duration-300 ${currentStep === step 
                  ? `${darkMode ? 'text-red-400' : 'text-blue-600'}` 
                  : 'text-gray-500 dark:text-zinc-500'}`}>
                  {step === 1 ? 'Contact Info' : step === 2 ? 'Topic & Schedule' : 'Materials & Goals'}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="animate-slideUp">
          <div className={`p-10 md:p-14 border-2 ${darkMode ? 'border-purple-600/40 bg-zinc-900/80 backdrop-blur-xl shadow-[0_0_60px_rgba(168,85,247,0.15)]' : 'border-purple-200 bg-white/90 backdrop-blur-xl shadow-2xl'} rounded-3xl relative overflow-hidden transition-all duration-500`}>
            <div className={`absolute top-0 right-0 w-64 h-64 ${darkMode ? 'bg-gradient-to-bl from-purple-600/20 to-transparent' : 'bg-gradient-to-bl from-purple-200/40 to-transparent'} rounded-full blur-3xl`}></div>
            <div className={`absolute bottom-0 left-0 w-64 h-64 ${darkMode ? 'bg-gradient-to-tr from-pink-600/20 to-transparent' : 'bg-gradient-to-tr from-pink-200/40 to-transparent'} rounded-full blur-3xl`}></div>
            
            <div className="relative">
              <form onSubmit={handleSubmit} className="space-y-8">
                {currentStep === 1 && (
                  <div className={`space-y-8 p-8 rounded-3xl border-2 shadow-xl relative overflow-hidden transition-all duration-500 ${darkMode ? 'border-blue-500/30 bg-zinc-900/60 backdrop-blur-sm' : 'border-blue-200 bg-gradient-to-br from-blue-50/80 to-cyan-50/80 backdrop-blur-sm'}`}>
                    <div className={`absolute -top-12 -right-12 w-48 h-48 ${darkMode ? 'bg-blue-600/10' : 'bg-blue-300/20'} rounded-full blur-3xl`}></div>
                    
                    <h3 className={`text-3xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'} flex items-center gap-4`}>
                      <div className={`w-12 h-12 bg-gradient-to-br ${darkMode ? 'from-blue-600 to-cyan-600 shadow-[0_0_20px_rgba(37,99,235,0.4)]' : 'from-blue-500 to-cyan-500 shadow-lg'} rounded-xl flex items-center justify-center transition-transform hover:scale-110 duration-300`}>
                        <User className="w-6 h-6 text-white" />
                      </div>
                      Contact Information
                    </h3>

                    {/* Parent/Guardian Information */}
                    <div className={`pb-8 ${darkMode ? 'border-blue-500/20' : 'border-blue-200'}`}>
                      <h4 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'} mb-6 flex items-center gap-2`}>
                        Parent/Guardian Information *
                        <span className={`px-3 py-1 text-xs font-bold rounded-full ${darkMode ? 'bg-red-900/30 text-red-300' : 'bg-red-100 text-red-700'}`}>
                          Required
                        </span>
                      </h4>
                      <div className="grid md:grid-cols-2 gap-8">
                        <div className="group">
                          <label className={`block text-sm font-bold ${darkMode ? 'text-zinc-300' : 'text-gray-700'} mb-3 transition-colors`}>
                            Parent First Name *
                          </label>
                          <input
                            type="text"
                            name="parentFirstName"
                            value={formData.parentFirstName}
                            onChange={handleInputChange}
                            className={`w-full px-5 py-4 border-2 ${darkMode ? 'border-blue-600/40 focus:border-blue-400 focus:ring-blue-500/30 bg-zinc-900/90 text-white placeholder-zinc-500' : 'border-blue-300 focus:border-blue-500 focus:ring-blue-300 bg-white text-gray-900 placeholder-gray-400'} rounded-xl focus:ring-4 transition-all shadow-sm hover:shadow-md duration-300`}
                            placeholder="Jane"
                          />
                          {errors.parentFirstName && <p className="text-red-500 text-sm mt-2 font-medium">{errors.parentFirstName}</p>}
                        </div>

                        <div className="group">
                          <label className={`block text-sm font-bold ${darkMode ? 'text-zinc-300' : 'text-gray-700'} mb-3 transition-colors`}>
                            Parent Last Name *
                          </label>
                          <input
                            type="text"
                            name="parentLastName"
                            value={formData.parentLastName}
                            onChange={handleInputChange}
                            className={`w-full px-5 py-4 border-2 ${darkMode ? 'border-blue-600/40 focus:border-blue-400 focus:ring-blue-500/30 bg-zinc-900/90 text-white placeholder-zinc-500' : 'border-blue-300 focus:border-blue-500 focus:ring-blue-300 bg-white text-gray-900 placeholder-gray-400'} rounded-xl focus:ring-4 transition-all shadow-sm hover:shadow-md duration-300`}
                            placeholder="Doe"
                          />
                          {errors.parentLastName && <p className="text-red-500 text-sm mt-2 font-medium">{errors.parentLastName}</p>}
                        </div>

                        <div className="group">
                          <label className={`block text-sm font-bold ${darkMode ? 'text-zinc-300' : 'text-gray-700'} mb-3 transition-colors`}>
                            Parent/Guardian Email *
                          </label>
                          <input
                            type="email"
                            name="parentEmail"
                            value={formData.parentEmail}
                            onChange={handleInputChange}
                            className={`w-full px-5 py-4 border-2 ${darkMode ? 'border-blue-600/40 focus:border-blue-400 focus:ring-blue-500/30 bg-zinc-900/90 text-white placeholder-zinc-500' : 'border-blue-300 focus:border-blue-500 focus:ring-blue-300 bg-white text-gray-900 placeholder-gray-400'} rounded-xl focus:ring-4 transition-all shadow-sm hover:shadow-md duration-300`}
                            placeholder="jane@example.com"
                          />
                          {errors.parentEmail && <p className="text-red-500 text-sm mt-2 font-medium">{errors.parentEmail}</p>}
                        </div>

                        <div className="group">
                          <label className={`block text-sm font-bold ${darkMode ? 'text-zinc-300' : 'text-gray-700'} mb-3 transition-colors`}>
                            Parent/Guardian Phone *
                          </label>
                          <input
                            type="tel"
                            name="parentPhone"
                            value={formData.parentPhone}
                            onChange={handleInputChange}
                            maxLength={15}
                            className={`w-full px-5 py-4 border-2 ${darkMode ? 'border-blue-600/40 focus:border-blue-400 focus:ring-blue-500/30 bg-zinc-900/90 text-white placeholder-zinc-500' : 'border-blue-300 focus:border-blue-500 focus:ring-blue-300 bg-white text-gray-900 placeholder-gray-400'} rounded-xl focus:ring-4 transition-all shadow-sm hover:shadow-md duration-300`}
                            placeholder="(555) 123-4567"
                            pattern="[0-9\s\-\(\)]*"
                            inputMode="tel"
                          />
                          {errors.parentPhone && <p className="text-red-500 text-sm mt-2 font-medium">{errors.parentPhone}</p>}
                        </div>
                      </div>
                    </div>

                    {/* Student Information */}
                    <div className={`pt-8 border-t-2 ${darkMode ? 'border-blue-500/20' : 'border-blue-200'}`}>
                      <h4 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'} mb-6 flex items-center gap-2`}>
                        Student Information *
                        <span className={`px-3 py-1 text-xs font-bold rounded-full ${darkMode ? 'bg-red-900/30 text-red-300' : 'bg-red-100 text-red-700'}`}>
                          Required
                        </span>
                      </h4>
                      <div className="grid md:grid-cols-2 gap-8">
                        <div className="group">
                          <label className={`block text-sm font-bold ${darkMode ? 'text-zinc-300' : 'text-gray-700'} mb-3 transition-colors`}>
                            Student First Name *
                          </label>
                          <input
                            type="text"
                            name="studentFirstName"
                            value={formData.studentFirstName}
                            onChange={handleInputChange}
                            className={`w-full px-5 py-4 border-2 ${darkMode ? 'border-blue-600/40 focus:border-blue-400 focus:ring-blue-500/30 bg-zinc-900/90 text-white placeholder-zinc-500' : 'border-blue-300 focus:border-blue-500 focus:ring-blue-300 bg-white text-gray-900 placeholder-gray-400'} rounded-xl focus:ring-4 transition-all shadow-sm hover:shadow-md duration-300`}
                            placeholder="John"
                          />
                          {errors.studentFirstName && <p className="text-red-500 text-sm mt-2 font-medium">{errors.studentFirstName}</p>}
                        </div>

                        <div className="group">
                          <label className={`block text-sm font-bold ${darkMode ? 'text-zinc-300' : 'text-gray-700'} mb-3 transition-colors`}>
                            Student Last Name *
                          </label>
                          <input
                            type="text"
                            name="studentLastName"
                            value={formData.studentLastName}
                            onChange={handleInputChange}
                            className={`w-full px-5 py-4 border-2 ${darkMode ? 'border-blue-600/40 focus:border-blue-400 focus:ring-blue-500/30 bg-zinc-900/90 text-white placeholder-zinc-500' : 'border-blue-300 focus:border-blue-500 focus:ring-blue-300 bg-white text-gray-900 placeholder-gray-400'} rounded-xl focus:ring-4 transition-all shadow-sm hover:shadow-md duration-300`}
                            placeholder="Doe"
                          />
                          {errors.studentLastName && <p className="text-red-500 text-sm mt-2 font-medium">{errors.studentLastName}</p>}
                        </div>

                        <div className="group">
                          <label className={`block text-sm font-bold ${darkMode ? 'text-zinc-300' : 'text-gray-700'} mb-3 transition-colors`}>
                            Grade Level *
                          </label>
                          <select
                            name="gradeLevel"
                            value={formData.gradeLevel}
                            onChange={handleInputChange}
                            className={`w-full px-5 py-4 border-2 ${darkMode ? 'border-blue-600/40 focus:border-blue-400 focus:ring-blue-500/30 bg-zinc-900/90 text-white' : 'border-blue-300 focus:border-blue-500 focus:ring-blue-300 bg-white text-gray-900'} rounded-xl focus:ring-4 transition-all shadow-sm hover:shadow-md duration-300`}
                          >
                            <option value="">Select grade level</option>
                            {gradeLevels.map(grade => (
                              <option key={grade} value={grade}>{grade}</option>
                            ))}
                          </select>
                          {errors.gradeLevel && <p className="text-red-500 text-sm mt-2 font-medium">{errors.gradeLevel}</p>}
                        </div>

                        <div className="group">
                          <label className={`block text-sm font-bold ${darkMode ? 'text-zinc-300' : 'text-gray-700'} mb-3 transition-colors`}>
                            Student Email *
                          </label>
                          <input
                            type="email"
                            name="studentEmail"
                            value={formData.studentEmail}
                            onChange={handleInputChange}
                            className={`w-full px-5 py-4 border-2 ${darkMode ? 'border-blue-600/40 focus:border-blue-400 focus:ring-blue-500/30 bg-zinc-900/90 text-white placeholder-zinc-500' : 'border-blue-300 focus:border-blue-500 focus:ring-blue-300 bg-white text-gray-900 placeholder-gray-400'} rounded-xl focus:ring-4 transition-all shadow-sm hover:shadow-md duration-300`}
                            placeholder="john@example.com"
                          />
                          {errors.studentEmail && <p className="text-red-500 text-sm mt-2 font-medium">{errors.studentEmail}</p>}
                        </div>

                        <div className="group">
                          <label className={`block text-sm font-bold ${darkMode ? 'text-zinc-300' : 'text-gray-700'} mb-3 transition-colors`}>
                            Student Phone (Optional)
                          </label>
                          <input
                            type="tel"
                            name="studentPhone"
                            value={formData.studentPhone}
                            onChange={handleInputChange}
                            maxLength={15}
                            className={`w-full px-5 py-4 border-2 ${darkMode ? 'border-blue-600/40 focus:border-blue-400 focus:ring-blue-500/30 bg-zinc-900/90 text-white placeholder-zinc-500' : 'border-blue-300 focus:border-blue-500 focus:ring-blue-300 bg-white text-gray-900 placeholder-gray-400'} rounded-xl focus:ring-4 transition-all shadow-sm hover:shadow-md duration-300`}
                            placeholder="(555) 123-4567"
                            pattern="[0-9\s\-\(\)]*"
                            inputMode="tel"
                          />
                          {errors.studentPhone && <p className="text-red-500 text-sm mt-2 font-medium">{errors.studentPhone}</p>}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {currentStep === 2 && (
                  <div className="space-y-8">
                    {/* Select Topic Section - Always visible */}
                    <div id="topic-section" className={`space-y-6 p-8 rounded-3xl border-2 shadow-xl ${darkMode ? 'border-emerald-600/30 bg-zinc-900/60 backdrop-blur-sm' : 'border-green-200 bg-gradient-to-br from-green-50/80 to-emerald-50/80 backdrop-blur-sm'}`}>
                      <div className="flex items-center justify-between">
                        <h3 className={`text-3xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'} flex items-center gap-4`}>
                          <div className={`w-12 h-12 bg-gradient-to-br ${darkMode ? 'from-emerald-600 to-emerald-700 shadow-[0_0_20px_rgba(16,185,129,0.4)]' : 'from-green-500 to-emerald-500 shadow-lg'} rounded-xl flex items-center justify-center transition-transform hover:scale-110 duration-300`}>
                            <BookOpen className="w-6 h-6 text-white" />
                          </div>
                          {bookedSessions.length > 0 ? 'Add Another Session' : 'Choose Your Subject'}
                        </h3>
                        {bookedSessions.length > 0 && (
                          <div className={`px-4 py-2 rounded-full ${darkMode ? 'bg-green-900/30 text-green-300' : 'bg-green-100 text-green-700'} font-bold`}>
                            {bookedSessions.length} session{bookedSessions.length !== 1 ? 's' : ''} booked
                          </div>
                        )}
                      </div>
                      
                      <p className={`text-lg ${darkMode ? 'text-zinc-300' : 'text-gray-600'}`}>
                        {bookedSessions.length > 0 
                          ? 'Select a subject for your next session. You can book as many sessions as you need.'
                          : 'Select the subject you need help with. Each subject has expert tutors available.'}
                      </p>
                      
                      {/* Topics Grid */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                        {[
                          { 
                            name: "Math", 
                            icon: "∫", 
                            description: "Algebra, Calculus, Geometry",
                            color: darkMode ? "from-blue-600 via-cyan-500 to-sky-400" : "from-blue-500 via-cyan-400 to-sky-300",
                            bgColor: darkMode ? "bg-blue-900/30" : "bg-blue-50",
                            borderColor: darkMode ? "border-blue-500/40" : "border-blue-200"
                          },
                          { 
                            name: "English", 
                            icon: "📚", 
                            description: "Literature, Writing, Grammar",
                            color: darkMode ? "from-rose-600 via-pink-500 to-rose-400" : "from-rose-500 via-pink-400 to-rose-300",
                            bgColor: darkMode ? "bg-rose-900/30" : "bg-rose-50",
                            borderColor: darkMode ? "border-rose-500/40" : "border-rose-200"
                          },
                          { 
                            name: "History", 
                            icon: "🏛️", 
                            description: "World History, US History",
                            color: darkMode ? "from-amber-600 via-yellow-500 to-orange-400" : "from-amber-500 via-yellow-400 to-orange-300",
                            bgColor: darkMode ? "bg-amber-900/30" : "bg-amber-50",
                            borderColor: darkMode ? "border-amber-500/40" : "border-amber-200"
                          },
                          { 
                            name: "Biology", 
                            icon: "🧬", 
                            description: "AP Bio, Cellular Biology",
                            color: darkMode ? "from-green-600 via-emerald-500 to-teal-400" : "from-green-500 via-emerald-400 to-teal-300",
                            bgColor: darkMode ? "bg-green-900/30" : "bg-green-50",
                            borderColor: darkMode ? "border-green-500/40" : "border-green-200"
                          },
                          { 
                            name: "Chemistry", 
                            icon: "⚗️", 
                            description: "Organic, Inorganic, AP Chem",
                            color: darkMode ? "from-purple-600 via-violet-500 to-fuchsia-400" : "from-purple-500 via-violet-400 to-fuchsia-300",
                            bgColor: darkMode ? "bg-purple-900/30" : "bg-purple-50",
                            borderColor: darkMode ? "border-purple-500/40" : "border-purple-200"
                          },
                          { 
                            name: "Physics", 
                            icon: "⚛️", 
                            description: "Mechanics, Electricity, AP Physics",
                            color: darkMode ? "from-indigo-600 via-blue-500 to-cyan-400" : "from-indigo-500 via-blue-400 to-cyan-300",
                            bgColor: darkMode ? "bg-indigo-900/30" : "bg-indigo-50",
                            borderColor: darkMode ? "border-indigo-500/40" : "border-indigo-200"
                          },
                          { 
                            name: "Computer Science", 
                            icon: "💻", 
                            description: "Programming, Algorithms",
                            color: darkMode ? "from-gray-600 via-zinc-500 to-neutral-400" : "from-gray-500 via-zinc-400 to-neutral-300",
                            bgColor: darkMode ? "bg-zinc-900/30" : "bg-gray-50",
                            borderColor: darkMode ? "border-zinc-500/40" : "border-gray-200"
                          }
                        ].map(subject => {
                          const isSelected = formData.selectedTopic === subject.name;
                          const tutorCount = tutors.filter(t => 
                            t.subjects && 
                            t.subjects.some(s => s.toLowerCase().includes(subject.name.toLowerCase()))
                          ).length;
                          
                          return (
                            <div
                              key={subject.name}
                              className={`group relative p-5 rounded-2xl border-3 transition-all duration-500 cursor-pointer 
                                ${isSelected 
                                  ? `${darkMode 
                                      ? 'shadow-[0_0_30px_rgba(239,68,68,0.4)] border-red-500/60' 
                                      : 'shadow-[0_0_30px_rgba(59,130,246,0.3)] border-blue-500/60'} scale-[1.02]` 
                                  : `${subject.borderColor} hover:scale-[1.02] hover:shadow-xl`
                                } 
                                ${subject.bgColor} overflow-hidden`}
                              onClick={() => handleTopicSelect(subject.name)}
                            >
                              <div className={`absolute inset-0 opacity-0 group-hover:opacity-10 transition-opacity duration-500 bg-gradient-to-br ${subject.color}`}></div>
                              
                              {isSelected && (
                                <div className="absolute top-3 right-3">
                                  <div className={`w-7 h-7 rounded-full flex items-center justify-center ${darkMode ? 'bg-red-500' : 'bg-blue-500'} shadow-lg`}>
                                    <CheckCircle className="w-4 h-4 text-white" />
                                  </div>
                                </div>
                              )}
                              
                              <div className={`w-16 h-16 rounded-2xl mb-4 flex items-center justify-center text-3xl 
                                ${isSelected 
                                  ? `${darkMode ? 'bg-gradient-to-br from-red-500 to-orange-500' : 'bg-gradient-to-br from-blue-500 to-purple-500'} text-white shadow-lg` 
                                  : `${darkMode ? 'bg-zinc-800' : 'bg-white'} shadow-md`
                                }`}>
                                {subject.icon}
                              </div>
                              
                              <h4 className={`text-xl font-bold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                                {subject.name === "Computer Science" ? "Comp. Sci." : subject.name}
                              </h4>
                              
                              <p className={`text-sm mb-3 ${darkMode ? 'text-zinc-400' : 'text-gray-600'}`}>
                                {subject.description}
                              </p>
                              
                              <div className="flex items-center justify-between mt-4">
                                <div className={`px-3 py-1.5 rounded-full text-xs font-bold ${
                                  tutorCount > 0 
                                    ? `${darkMode ? 'bg-green-900/40 text-green-300' : 'bg-green-100 text-green-700'}`
                                    : `${darkMode ? 'bg-yellow-900/40 text-yellow-300' : 'bg-yellow-100 text-yellow-700'}`
                                }`}>
                                  {tutorCount > 0 
                                    ? `✓ ${tutorCount} tutor${tutorCount !== 1 ? 's' : ''}`
                                    : "No tutors"
                                  }
                                </div>
                                
                                <div className={`transform transition-transform duration-300 ${isSelected ? 'translate-x-0' : '-translate-x-2 opacity-0 group-hover:translate-x-0 group-hover:opacity-100'}`}>
                                  <ChevronRight className={`w-5 h-5 ${darkMode ? 'text-zinc-400' : 'text-gray-400'}`} />
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Tutor Selection - Only show when a topic is selected */}
                    {formData.selectedTopic && (
                      <div className={`space-y-6 p-8 rounded-3xl border-2 shadow-xl ${darkMode ? 'border-purple-600/40 bg-zinc-900/60 backdrop-blur-sm' : 'border-purple-200 bg-gradient-to-br from-purple-50/80 to-pink-50/80 backdrop-blur-sm'}`}>
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                          <div>
                            <h3 className={`text-3xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'} flex items-center gap-4 mb-2`}>
                              <div className={`w-12 h-12 bg-gradient-to-br ${darkMode ? 'from-purple-600 to-pink-600 shadow-[0_0_20px_rgba(168,85,247,0.4)]' : 'from-purple-500 to-pink-500 shadow-lg'} rounded-xl flex items-center justify-center`}>
                                <Sparkles className="w-6 h-6 text-white" />
                              </div>
                              Select a Tutor for {formData.selectedTopic}
                            </h3>
                            <p className={`${darkMode ? 'text-zinc-400' : 'text-gray-600'}`}>
                              Click on a tutor to schedule a session with them.
                            </p>
                          </div>
                          <div className={`px-4 py-2 rounded-full ${darkMode ? 'bg-zinc-800 border border-zinc-700' : 'bg-white border border-gray-200'} shadow-sm`}>
                            <span className={`text-sm font-medium ${darkMode ? 'text-zinc-300' : 'text-gray-700'}`}>
                              {filteredTutors.length} tutor{filteredTutors.length !== 1 ? 's' : ''} available
                            </span>
                          </div>
                        </div>
                        
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                          {filteredTutors.length > 0 ? (
                            filteredTutors.map(tutor => {
                              const tutorSubjects = tutor.subjects || [];
                              const totalSlots = tutor.availability ? 
                                Object.values(tutor.availability).reduce((sum, slots) => 
                                  sum + (Array.isArray(slots) ? slots.length : typeof slots === 'string' ? 1 : 0), 0
                                ) : 0;
                              
                              // Check if this tutor has any booked sessions
                              const tutorBookedSessions = bookedSessions.filter(s => s.tutorId === tutor.id);
                              
                              return (
                                <div
                                  key={tutor.id}
                                  className={`p-5 rounded-2xl border-3 cursor-pointer transition-all duration-300 hover:scale-[1.02] hover:shadow-lg ${
                                    tutorBookedSessions.length > 0
                                      ? `${darkMode 
                                          ? 'border-red-500 bg-red-900/20' 
                                          : 'border-blue-500 bg-blue-50'} shadow-lg` 
                                      : `${darkMode 
                                          ? 'border-zinc-700 bg-zinc-800/80 hover:border-purple-500/60' 
                                          : 'border-gray-300 bg-white hover:border-purple-300'} shadow-md`
                                  }`}
                                  onClick={() => openCalendarForTutor(tutor)}
                                >
                                  <div className="flex items-start gap-4">
                                    {/* Tutor Avatar */}
                                    <div className="relative flex-shrink-0">
                                      <div className="w-16 h-16 rounded-2xl overflow-hidden border-3 border-gray-200 dark:border-zinc-700">
                                        <img 
                                          src={tutor.photo_url} 
                                          alt={tutor.full_name || tutor.name}
                                          className="w-full h-full object-cover"
                                          onError={(e) => {
                                            e.target.src = defaultTutorImages.default;
                                          }}
                                        />
                                      </div>
                                      {/* Online Status Indicator */}
                                      <div className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full border-2 border-white dark:border-zinc-800 bg-gradient-to-r from-green-400 to-emerald-500 shadow-md"></div>
                                    </div>
                                    
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-start justify-between mb-3">
                                        <div>
                                          <h4 className={`font-bold text-xl ${darkMode ? 'text-white' : 'text-gray-900'} mb-1`}>
                                            {tutor.full_name || tutor.name}
                                          </h4>
                                          <div className="flex items-center gap-2">
                                            <GraduationCap className={`w-4 h-4 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`} />
                                            <span className={`text-sm ${darkMode ? 'text-zinc-400' : 'text-gray-600'}`}>
                                              {tutor.current_school || "Expert Tutor"}
                                            </span>
                                          </div>
                                        </div>
                                      </div>
                                      
                                      {/* Subject Badges */}
                                      <div className="mb-4">
                                        <div className="flex items-center gap-2 mb-2">
                                          <BookOpen className={`w-4 h-4 ${darkMode ? 'text-purple-400' : 'text-purple-600'}`} />
                                          <span className={`text-xs font-medium ${darkMode ? 'text-zinc-500' : 'text-gray-500'}`}>EXPERT IN:</span>
                                        </div>
                                        <div className="flex flex-wrap gap-2">
                                          {tutorSubjects.map((subject, idx) => {
                                            const subjectConfig = {
                                              Math: { color: darkMode ? 'bg-blue-900/40 text-blue-300' : 'bg-blue-100 text-blue-700' },
                                              Science: { color: darkMode ? 'bg-emerald-900/40 text-emerald-300' : 'bg-emerald-100 text-emerald-700' },
                                              English: { color: darkMode ? 'bg-rose-900/40 text-rose-300' : 'bg-rose-100 text-rose-700' },
                                              History: { color: darkMode ? 'bg-amber-900/40 text-amber-300' : 'bg-amber-100 text-amber-700' },
                                              Biology: { color: darkMode ? 'bg-green-900/40 text-green-300' : 'bg-green-100 text-green-700' },
                                              Chemistry: { color: darkMode ? 'bg-purple-900/40 text-purple-300' : 'bg-purple-100 text-purple-700' },
                                              Physics: { color: darkMode ? 'bg-indigo-900/40 text-indigo-300' : 'bg-indigo-100 text-indigo-700' },
                                              "Computer Science": { color: darkMode ? 'bg-zinc-900/40 text-zinc-300' : 'bg-gray-100 text-gray-700' }
                                            };
                                            
                                            const subjectStyle = subjectConfig[subject] || { 
                                              color: darkMode ? 'bg-zinc-800 text-zinc-300' : 'bg-gray-100 text-gray-700' 
                                            };
                                            
                                            return (
                                              <span 
                                                key={idx} 
                                                className={`px-3 py-1.5 text-xs font-medium rounded-full ${
                                                  formData.selectedTopic === subject
                                                    ? darkMode ? 'ring-2 ring-red-500' : 'ring-2 ring-blue-500'
                                                    : ''
                                                } ${subjectStyle.color}`}
                                              >
                                                {subject === "Computer Science" ? "Comp. Sci." : subject}
                                              </span>
                                            );
                                          })}
                                        </div>
                                      </div>
                                      
                                      {/* Session Info */}
                                      <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200/50 dark:border-zinc-700/50">
                                        <div className="flex items-center gap-1.5">
                                          <Calendar className={`w-4 h-4 ${darkMode ? 'text-green-400' : 'text-green-600'}`} />
                                          <span className={`text-sm font-medium ${darkMode ? 'text-zinc-300' : 'text-gray-700'}`}>
                                            {totalSlots} slots
                                          </span>
                                        </div>
                                        
                                        {tutorBookedSessions.length > 0 && (
                                          <div className={`px-2 py-1 rounded-full text-xs font-bold ${
                                            darkMode ? 'bg-red-900/40 text-red-300' : 'bg-red-100 text-red-700'
                                          }`}>
                                            {tutorBookedSessions.length} booked
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                  
                                  {tutorBookedSessions.length > 0 && (
                                    <div className={`mt-4 p-3 rounded-xl text-center ${
                                      darkMode ? 'bg-red-900/30 border border-red-700/40' 
                                      : 'bg-blue-50 border border-blue-200'
                                    }`}>
                                      <p className={`text-sm font-medium flex items-center justify-center gap-2 ${
                                        darkMode ? 'text-red-300' : 'text-blue-700'
                                      }`}>
                                        <CheckCircle className="w-4 h-4" />
                                        {tutorBookedSessions.length} session{tutorBookedSessions.length !== 1 ? 's' : ''} booked
                                      </p>
                                    </div>
                                  )}
                                </div>
                              );
                            })
                          ) : (
                            <div className="col-span-3 text-center py-12">
                              <div className="w-20 h-20 mx-auto mb-4 bg-gradient-to-br from-yellow-100 to-orange-100 dark:from-yellow-900/20 dark:to-orange-900/20 rounded-2xl flex items-center justify-center">
                                <AlertCircle className={`w-10 h-10 ${darkMode ? 'text-yellow-400' : 'text-yellow-500'}`} />
                              </div>
                              <h4 className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'} mb-2`}>
                                No Tutors Available
                              </h4>
                              <p className={`max-w-md mx-auto ${darkMode ? 'text-zinc-400' : 'text-gray-600'}`}>
                                Currently there are no tutors available for {formData.selectedTopic}. 
                                Please select a different subject or check back later.
                              </p>
                              <button
                                onClick={() => setFormData(prev => ({ ...prev, selectedTopic: "" }))}
                                className={`mt-4 px-6 py-2 rounded-xl font-medium ${darkMode ? 'bg-zinc-800 text-white' : 'bg-gray-100 text-gray-700'}`}
                              >
                                Choose Different Subject
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Schedule Section */}
                    <div className={`space-y-6 p-8 rounded-3xl border-2 shadow-xl ${darkMode ? 'border-blue-600/40 bg-zinc-900/60 backdrop-blur-sm' : 'border-blue-200 bg-gradient-to-br from-blue-50/80 to-cyan-50/80 backdrop-blur-sm'}`}>
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className={`text-3xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'} flex items-center gap-4`}>
                            <div className={`w-12 h-12 bg-gradient-to-br ${darkMode ? 'from-blue-600 to-cyan-600 shadow-[0_0_20px_rgba(37,99,235,0.4)]' : 'from-blue-500 to-cyan-500 shadow-lg'} rounded-xl flex items-center justify-center transition-transform hover:scale-110 duration-300`}>
                              <Calendar className="w-6 h-6 text-white" />
                            </div>
                            Your Booked Sessions
                          </h3>
                          <p className={`mt-2 ${darkMode ? 'text-zinc-400' : 'text-gray-600'}`}>
                            {bookedSessions.length > 0 
                              ? `You have ${bookedSessions.length} session${bookedSessions.length !== 1 ? 's' : ''} booked. You can add more sessions below.`
                              : "Select a subject and tutor above to book your first session."
                            }
                          </p>
                        </div>
                        
                        {bookedSessions.length > 0 && (
                          <div className={`px-4 py-2 rounded-full ${darkMode ? 'bg-green-900/30 text-green-300' : 'bg-green-100 text-green-700'} font-bold`}>
                            Total: ${calculatePrice()}
                          </div>
                        )}
                      </div>

                      {errors.bookedSessions && (
                        <div className={`p-4 rounded-xl border-2 ${darkMode ? 'border-red-500/50 bg-red-900/20' : 'border-red-400 bg-red-50'}`}>
                          <p className="text-red-500 font-medium">{errors.bookedSessions}</p>
                        </div>
                      )}

                      {/* Booked Sessions Summary */}
                      {bookedSessions.length > 0 ? (
                        <div className={`space-y-4 ${darkMode ? 'bg-zinc-800/50' : 'bg-gray-50'} p-6 rounded-2xl border-2 ${darkMode ? 'border-green-500/30' : 'border-green-200'}`}>
                          <div className="flex items-center justify-between">
                            <h4 className={`font-bold text-xl ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                              Your Booked Sessions ({bookedSessions.length})
                            </h4>
                            <div className="flex items-center gap-3">
                              <span className={`text-sm ${darkMode ? 'text-zinc-400' : 'text-gray-600'}`}>
                                {Array.from(new Set(bookedSessions.map(s => s.tutorId))).length} tutor{Array.from(new Set(bookedSessions.map(s => s.tutorId))).length !== 1 ? 's' : ''}
                              </span>
                            </div>
                          </div>
                          
                          <div className="space-y-3 max-h-60 overflow-y-auto custom-scrollbar">
                            {bookedSessions.map((session, index) => (
                              <div key={session.key} className={`p-4 rounded-xl border-2 ${darkMode ? 'border-emerald-500/30 bg-zinc-800/60' : 'border-emerald-200 bg-white'}`}>
                                <div className="flex items-start justify-between">
                                  <div className="flex items-start gap-3 flex-1">
                                    <img 
                                      src={session.tutorImage} 
                                      alt={session.tutorName}
                                      className="w-12 h-12 rounded-lg object-cover"
                                      onError={(e) => {
                                        e.target.src = defaultTutorImages.default;
                                      }}
                                    />
                                    <div className="flex-1">
                                      <h5 className={`font-bold ${darkMode ? 'text-white' : 'text-gray-900'} mb-1`}>
                                        Session {index + 1}: {session.subject}
                                      </h5>
                                      <div className="flex flex-wrap items-center gap-4 text-sm">
                                        <div className="flex items-center gap-2">
                                          <User className={`w-4 h-4 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`} />
                                          <span className={darkMode ? 'text-zinc-300' : 'text-gray-700'}>{session.tutorName}</span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                          <Calendar className={`w-4 h-4 ${darkMode ? 'text-red-400' : 'text-red-600'}`} />
                                          <span className={darkMode ? 'text-zinc-300' : 'text-gray-700'}>
                                            {new Date(session.date + 'T00:00:00').toLocaleDateString('en-US', { 
                                              month: 'short', 
                                              day: 'numeric',
                                              year: 'numeric'
                                            })}
                                          </span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                          <Clock className={`w-4 h-4 ${darkMode ? 'text-orange-400' : 'text-orange-600'}`} />
                                          <span className={darkMode ? 'text-zinc-300' : 'text-gray-700'}>{formatTimeForDisplay(session.time)}</span>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => removeBookedSession(index)}
                                    className={`p-2 rounded-lg ${darkMode ? 'hover:bg-red-900/30' : 'hover:bg-red-100'} ml-2 flex-shrink-0`}
                                  >
                                    <X className="w-5 h-5 text-red-500" />
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <div className={`p-6 rounded-2xl border-2 ${darkMode ? 'border-purple-500/30 bg-purple-900/20' : 'border-purple-200 bg-purple-50'}`}>
                          <div className="text-center">
                            <BookOpen className={`w-16 h-16 mx-auto mb-4 ${darkMode ? 'text-purple-400' : 'text-purple-600'}`} />
                            <h4 className={`text-xl font-bold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                              No Sessions Booked Yet
                            </h4>
                            <p className={`mb-4 ${darkMode ? 'text-zinc-300' : 'text-gray-600'}`}>
                              Select a subject and tutor above to book your first session.
                            </p>
                          </div>
                        </div>
                      )}

                      {/* Add Another Session Button - Always visible when there are sessions */}
                      {bookedSessions.length > 0 && (
                        <div className="flex justify-center pt-4">
                          <button
                            type="button"
                            onClick={handleAddNewSession}
                            className={`flex items-center gap-3 px-8 py-4 rounded-xl font-bold transition-all duration-300 ${darkMode 
                              ? 'bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white shadow-lg hover:shadow-xl' 
                              : 'bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white shadow-md hover:shadow-lg'
                            } hover:scale-105`}
                          >
                            <span className="text-2xl">+</span>
                            <span className="text-lg">Add Another Session</span>
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Calendar Modal */}
                    {showCalendar && selectedTutorForCalendar && (
                      <div id="schedule-section" className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                        <div
                          onClick={(e) => e.stopPropagation()}
                          className={`max-w-4xl w-full max-h-[90vh] overflow-y-auto rounded-3xl shadow-2xl ${darkMode ? 'bg-zinc-900' : 'bg-white'}`}
                        >
                          {/* Modal Header */}
                          <div className={`p-6 rounded-t-3xl ${darkMode ? 'bg-gradient-to-r from-red-600 to-orange-600' : 'bg-gradient-to-r from-blue-600 to-purple-600'} text-white`}>
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-4">
                                <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
                                  <Calendar className="w-8 h-8 text-white" />
                                </div>
                                <div>
                                  <h2 className="text-3xl font-bold">Schedule with {selectedTutorForCalendar.full_name || selectedTutorForCalendar.name}</h2>
                                  <p className="opacity-90">Select a date to view available time slots</p>
                                </div>
                              </div>
                              <button
                                onClick={() => setShowCalendar(false)}
                                className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white/30 transition-colors"
                              >
                                <X className="w-6 h-6" />
                              </button>
                            </div>
                          </div>

                          <div className="p-6">
                            <div className="grid lg:grid-cols-2 gap-6">
                              {/* Calendar */}
                              <div>
                                <div className="flex items-center justify-between mb-6">
                                  <button 
                                    onClick={() => {
                                      const newDate = new Date(currentCalendarYear, currentCalendarMonth - 1, 1);
                                      const now = new Date();
                                      if (newDate.getMonth() >= now.getMonth() || newDate.getFullYear() > now.getFullYear()) {
                                        setCurrentCalendarYear(newDate.getFullYear());
                                        setCurrentCalendarMonth(newDate.getMonth());
                                        setSelectedDate(null);
                                      }
                                    }}
                                    className={`w-10 h-10 rounded-lg flex items-center justify-center transition-colors ${darkMode ? 'bg-zinc-800 hover:bg-zinc-700' : 'bg-gray-100 hover:bg-gray-200'}`}
                                  >
                                    <ChevronLeft className={`w-5 h-5 ${darkMode ? 'text-zinc-300' : 'text-gray-700'}`} />
                                  </button>
                                  <h3 className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                                    {getMonthName(currentCalendarYear, currentCalendarMonth)}
                                  </h3>
                                  <button 
                                    onClick={() => {
                                      const newDate = new Date(currentCalendarYear, currentCalendarMonth + 1, 1);
                                      const maxDate = new Date();
                                      maxDate.setDate(maxDate.getDate() + 40);
                                      
                                      if (newDate <= maxDate) {
                                        setCurrentCalendarYear(newDate.getFullYear());
                                        setCurrentCalendarMonth(newDate.getMonth());
                                        setSelectedDate(null);
                                      }
                                    }}
                                    className={`w-10 h-10 rounded-lg flex items-center justify-center transition-colors ${darkMode ? 'bg-zinc-800 hover:bg-zinc-700' : 'bg-gray-100 hover:bg-gray-200'}`}
                                  >
                                    <ChevronRight className={`w-5 h-5 ${darkMode ? 'text-zinc-300' : 'text-gray-700'}`} />
                                  </button>
                                </div>

                                <div className="grid grid-cols-7 gap-2 mb-2">
                                  {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                                    <div key={day} className={`text-center text-sm font-bold h-8 flex items-center justify-center ${darkMode ? 'text-zinc-400' : 'text-gray-600'}`}>
                                      {day}
                                    </div>
                                  ))}
                                </div>
                                <div className="grid grid-cols-7 gap-2">
                                  {renderCalendar()}
                                </div>

                                {/* Legend */}
                                <div className="mt-6 flex flex-wrap gap-4 text-sm">
                                  <div className="flex items-center gap-2">
                                    <div className={`w-4 h-4 rounded border-2 ${darkMode ? 'bg-gradient-to-r from-green-700 to-emerald-700 border-green-600' : 'bg-gradient-to-r from-green-100 to-emerald-100 border-green-300'}`}></div>
                                    <span className={darkMode ? 'text-zinc-300' : 'text-gray-600'}>Available</span>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <div className={`w-4 h-4 rounded ${darkMode ? 'bg-gradient-to-r from-red-500 to-orange-500' : 'bg-gradient-to-r from-blue-400 to-purple-400'}`}></div>
                                    <span className={darkMode ? 'text-zinc-300' : 'text-gray-600'}>Selected</span>
                                  </div>
                                </div>
                              </div>

                              {/* Time Slots */}
                              <div>
                                {selectedDate ? (
                                  <div>
                                    <div className={`rounded-2xl p-4 mb-6 border-2 ${darkMode ? 'border-red-500/40 bg-gradient-to-r from-zinc-800 to-zinc-900' : 'border-blue-200 bg-gradient-to-r from-blue-50 to-purple-50'}`}>
                                      <div className="flex items-center gap-2 mb-2">
                                        <Calendar className={`w-5 h-5 ${darkMode ? 'text-red-400' : 'text-blue-600'}`} />
                                        <span className={`font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Selected Date:</span>
                                      </div>
                                      <p className={`text-xl font-bold ${darkMode ? 'text-red-400' : 'text-blue-600'}`}>
                                        {new Date(selectedDate + 'T00:00:00').toLocaleDateString('en-US', { 
                                          weekday: 'long', 
                                          month: 'long', 
                                          day: 'numeric',
                                          year: 'numeric'
                                        })}
                                      </p>
                                    </div>

                                    <h4 className={`text-lg font-bold mb-4 flex items-center gap-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                                      <Clock className={`w-5 h-5 ${darkMode ? 'text-orange-400' : 'text-purple-600'}`} />
                                      Available Time Slots
                                    </h4>
                                    {availableSlotsByDate[selectedDate] && availableSlotsByDate[selectedDate].length > 0 ? (
                                      <div className="grid grid-cols-2 gap-3 max-h-96 overflow-y-auto custom-scrollbar">
                                        {availableSlotsByDate[selectedDate]?.map((timeSlot, idx) => {
                                          const isBooked = bookedSessions.some(
                                            s => s.tutorId === selectedTutorForCalendar.id && 
                                                 s.date === selectedDate && 
                                                 s.time === timeSlot
                                          );
                                          
                                          return (
                                            <button
                                              key={idx}
                                              type="button"
                                              onClick={() => !isBooked && handleTimeSlotSelect(timeSlot)}
                                              disabled={isBooked}
                                              className={`font-bold py-4 px-4 rounded-xl shadow-md hover:shadow-xl transition-all hover:scale-105 duration-300 ${
                                                isBooked
                                                  ? darkMode 
                                                    ? 'bg-zinc-700 text-zinc-400 cursor-not-allowed'
                                                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                                  : darkMode
                                                  ? 'bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white'
                                                  : 'bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 text-white'
                                              }`}
                                            >
                                              <div className="flex items-center justify-center gap-2">
                                                <Clock className="w-4 h-4" />
                                                <span>{formatTimeForDisplay(timeSlot)}</span>
                                                {isBooked && <X className="w-4 h-4 ml-1" />}
                                              </div>
                                            </button>
                                          );
                                        })}
                                      </div>
                                    ) : (
                                      <div className={`p-4 rounded-xl border-2 ${darkMode ? 'bg-yellow-900/20 border-yellow-500/30' : 'bg-yellow-50 border-yellow-200'}`}>
                                        <p className={darkMode ? 'text-yellow-300' : 'text-yellow-700'}>
                                          No time slots available for this date. Please select a different date.
                                        </p>
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                  <div className="h-full flex items-center justify-center py-16">
                                    <div className="text-center">
                                      <div className={`w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-4 ${darkMode ? 'bg-red-500/20' : 'bg-blue-100'}`}>
                                        <Calendar className={`w-12 h-12 ${darkMode ? 'text-red-400' : 'text-blue-600'}`} />
                                      </div>
                                      <p className={`text-lg ${darkMode ? 'text-zinc-300' : 'text-gray-600'}`}>
                                        Select a date from the calendar to view available time slots
                                      </p>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {currentStep === 3 && (
                  <div className="space-y-8">
                    {/* Upload Files */}
                    <div className={`space-y-6 p-8 rounded-3xl border-2 shadow-xl ${darkMode ? 'border-rose-600/40 bg-zinc-900/60 backdrop-blur-sm' : 'border-pink-200 bg-gradient-to-br from-pink-50/80 to-rose-50/80 backdrop-blur-sm'}`} data-error="files">
                      <div className="flex items-center justify-between">
                        <h3 className={`text-3xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'} flex items-center gap-4`}>
                          <div className={`w-12 h-12 bg-gradient-to-br ${darkMode ? 'from-rose-500 to-pink-500 shadow-[0_0_20px_rgba(244,63,94,0.4)]' : 'from-pink-500 to-rose-500 shadow-lg'} rounded-xl flex items-center justify-center transition-transform hover:scale-110 duration-300`}>
                            <Upload className="w-6 h-6 text-white" />
                          </div>
                          Upload Study Materials *
                        </h3>
                        <span className={`px-3 py-1 text-xs font-bold rounded-full ${darkMode ? 'bg-red-900/30 text-red-300' : 'bg-red-100 text-red-700'}`}>
                          Required
                        </span>
                      </div>
                      <p className={`${darkMode ? 'text-zinc-400' : 'text-gray-600'}`}>Upload at least one study material (notes, syllabus, textbook pages, etc.) so your tutor can refresh their memory.</p>
                      
                      <div className={`border-3 border-dashed rounded-2xl p-10 text-center transition-colors duration-300 ${darkMode ? errors.files ? 'border-red-500/50 hover:border-red-400 bg-red-900/20' : 'border-rose-600/50 hover:border-rose-400 bg-zinc-900/50' : errors.files ? 'border-red-400 hover:border-red-500 bg-red-50' : 'border-pink-300 hover:border-pink-400 bg-white/50'}`}>
                        <input
                          type="file"
                          multiple
                          onChange={handleFileUpload}
                          className="hidden"
                          id="file-upload"
                          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.txt"
                        />
                        <label htmlFor="file-upload" className="cursor-pointer">
                          <Upload className={`w-16 h-16 mx-auto mb-4 ${errors.files ? 'text-red-500 dark:text-red-400' : darkMode ? 'text-rose-400' : 'text-pink-400'}`} />
                          <p className={`font-bold text-lg mb-2 ${errors.files ? 'text-red-700 dark:text-red-300' : darkMode ? 'text-zinc-300' : 'text-gray-700'}`}>
                            {files.length > 0 ? 'Click to add more files' : 'Click to upload or drag and drop'}
                          </p>
                          <p className={`text-sm ${errors.files ? 'text-red-600 dark:text-red-400' : darkMode ? 'text-zinc-400' : 'text-gray-600'}`}>
                            PDF, DOC, DOCX, JPG, PNG, TXT (Max 10MB each) - Required
                          </p>
                          {files.length > 0 && (
                            <p className={`text-sm mt-2 font-medium ${darkMode ? 'text-emerald-400' : 'text-green-600'}`}>
                              ✓ {files.length} file{files.length !== 1 ? 's' : ''} uploaded
                            </p>
                          )}
                        </label>
                      </div>

                      {errors.files && <p className="text-red-500 text-sm mt-3 font-medium">{errors.files}</p>}

                      {files.length > 0 && (
                        <div className="mt-6 space-y-3">
                          <h4 className={`font-bold ${darkMode ? 'text-white' : 'text-gray-900'} mb-2`}>Uploaded Files:</h4>
                          {files.map((file, index) => (
                            <div
                              key={index}
                              className={`flex items-center justify-between p-4 rounded-xl border-2 ${darkMode ? 'border-rose-500/30 bg-zinc-900' : 'border-pink-200 bg-white'}`}
                            >
                              <div className="flex items-center gap-3">
                                <FileText className={`w-6 h-6 ${darkMode ? 'text-rose-400' : 'text-pink-500'}`} />
                                <div>
                                  <span className={`font-medium ${darkMode ? 'text-zinc-300' : 'text-gray-700'}`}>
                                    {file.name}
                                  </span>
                                  <span className={`text-xs ml-3 ${darkMode ? 'text-zinc-500' : 'text-gray-500'}`}>
                                    {(file.size / 1024).toFixed(1)} KB
                                  </span>
                                </div>
                              </div>
                              <button
                                type="button"
                                onClick={() => removeFile(index)}
                                className={`p-2 rounded-lg transition-colors ${darkMode ? 'hover:bg-red-900/30' : 'hover:bg-red-100'}`}
                              >
                                <X className="w-5 h-5 text-red-500" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Learning Goals */}
                    <div className={`space-y-6 p-8 rounded-3xl border-2 shadow-xl ${darkMode ? 'border-emerald-600/40 bg-zinc-900/60 backdrop-blur-sm' : 'border-emerald-200 bg-gradient-to-br from-emerald-50/80 to-green-50/80 backdrop-blur-sm'}`} data-error="goals">
                      <div className="flex items-center justify-between">
                        <h3 className={`text-3xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'} flex items-center gap-4`}>
                          <div className={`w-12 h-12 bg-gradient-to-br ${darkMode ? 'from-emerald-500 to-green-500 shadow-[0_0_20px_rgba(16,185,129,0.4)]' : 'from-emerald-500 to-green-500 shadow-lg'} rounded-xl flex items-center justify-center transition-transform hover:scale-110 duration-300`}>
                            <TrendingUp className="w-6 h-6 text-white" />
                          </div>
                          Learning Goals *
                        </h3>
                        <span className={`px-3 py-1 text-xs font-bold rounded-full ${darkMode ? 'bg-red-900/30 text-red-300' : 'bg-red-100 text-red-700'}`}>
                          Required
                        </span>
                      </div>
                      <p className={`${darkMode ? 'text-zinc-400' : 'text-gray-600'}`}>
                        What do you hope to achieve with tutoring? (e.g., improve test scores, understand specific topics, prepare for exams, homework help)
                      </p>
                      <textarea
                        name="goals"
                        value={formData.goals}
                        onChange={handleInputChange}
                        rows="4"
                        className={`w-full px-5 py-4 border-2 ${errors.goals ? 'border-red-500 focus:border-red-500 focus:ring-red-500/30' : darkMode ? 'border-emerald-600/40 focus:border-emerald-400 focus:ring-emerald-500/30' : 'border-emerald-300 focus:border-emerald-500 focus:ring-emerald-300'} ${darkMode ? 'bg-zinc-900/90 text-white placeholder-zinc-500' : 'bg-white text-gray-900 placeholder-gray-400'} rounded-xl focus:ring-4 transition-all resize-none shadow-sm hover:shadow-md duration-300`}
                        placeholder="Please describe your learning goals in detail..."
                      />
                      {errors.goals && <p className="text-red-500 text-sm mt-2 font-medium">{errors.goals}</p>}
                      <div className="flex items-start gap-2 text-sm">
                        <Info className={`w-4 h-4 mt-0.5 ${darkMode ? 'text-blue-400' : 'text-blue-500'}`} />
                        <p className={`${darkMode ? 'text-blue-300' : 'text-blue-600'}`}>
                          Detailed learning goals help tutors prepare better for your sessions
                        </p>
                      </div>
                    </div>

                    {/* Additional Notes */}
                    <div className={`space-y-6 p-8 rounded-3xl border-2 shadow-xl ${darkMode ? 'border-cyan-600/40 bg-zinc-900/60 backdrop-blur-sm' : 'border-cyan-200 bg-gradient-to-br from-cyan-50/80 to-blue-50/80 backdrop-blur-sm'}`}>
                      <h3 className={`text-3xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'} flex items-center gap-4`}>
                        <div className={`w-12 h-12 bg-gradient-to-br ${darkMode ? 'from-cyan-500 to-blue-500 shadow-[0_0_20px_rgba(6,182,212,0.4)]' : 'from-cyan-500 to-blue-500 shadow-lg'} rounded-xl flex items-center justify-center transition-transform hover:scale-110 duration-300`}>
                          <Info className="w-6 h-6 text-white" />
                        </div>
                        Additional Notes (Optional)
                      </h3>
                      <textarea
                        name="additionalNotes"
                        value={formData.additionalNotes}
                        onChange={handleInputChange}
                        rows="3"
                        className={`w-full px-5 py-4 border-2 ${darkMode ? 'border-cyan-600/40 focus:border-cyan-400 focus:ring-cyan-500/30 bg-zinc-900/90 text-white placeholder-zinc-500' : 'border-cyan-300 focus:border-cyan-500 focus:ring-cyan-300 bg-white text-gray-900 placeholder-gray-400'} rounded-xl focus:ring-4 transition-all resize-none shadow-sm hover:shadow-md duration-300`}
                        placeholder="Any other information we should know? (e.g., learning style preferences, specific challenges, special needs)"
                      />
                    </div>

                    {/* Review Summary */}
                    <div className={`p-8 rounded-3xl border-2 shadow-xl ${darkMode ? 'border-purple-600/40 bg-zinc-900/60 backdrop-blur-sm' : 'border-purple-200 bg-gradient-to-br from-purple-50/80 to-pink-50/80 backdrop-blur-sm'}`}>
                      <h3 className={`text-3xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'} flex items-center gap-4 mb-6`}>
                        <div className={`w-12 h-12 bg-gradient-to-br ${darkMode ? 'from-purple-600 to-pink-600 shadow-[0_0_20px_rgba(168,85,247,0.4)]' : 'from-purple-500 to-pink-500 shadow-lg'} rounded-xl flex items-center justify-center transition-transform hover:scale-110 duration-300`}>
                          <CheckCircle className="w-6 h-6 text-white" />
                        </div>
                        Booking Summary
                      </h3>
                      
                      <div className="grid md:grid-cols-2 gap-8">
                        <div>
                          <h4 className={`font-bold text-lg mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Contact Information</h4>
                          <div className="space-y-3">
                            <p><span className={`font-semibold ${darkMode ? 'text-zinc-300' : 'text-gray-700'}`}>Parent:</span> {formData.parentFirstName} {formData.parentLastName}</p>
                            <p><span className={`font-semibold ${darkMode ? 'text-zinc-300' : 'text-gray-700'}`}>Student:</span> {formData.studentFirstName} {formData.studentLastName} ({formData.gradeLevel})</p>
                            <p><span className={`font-semibold ${darkMode ? 'text-zinc-300' : 'text-gray-700'}`}>Email:</span> {formData.parentEmail}</p>
                            <p><span className={`font-semibold ${darkMode ? 'text-zinc-300' : 'text-gray-700'}`}>Phone:</span> {formData.parentPhone}</p>
                          </div>
                        </div>
                        
                        <div>
                          <h4 className={`font-bold text-lg mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Session Details</h4>
                          <div className="space-y-3">
                            <p><span className={`font-semibold ${darkMode ? 'text-zinc-300' : 'text-gray-700'}`}>Total Sessions:</span> {bookedSessions.length}</p>
                            <p><span className={`font-semibold ${darkMode ? 'text-zinc-300' : 'text-gray-700'}`}>Total Tutors:</span> {Array.from(new Set(bookedSessions.map(s => s.tutorId))).length}</p>
                            <p><span className={`font-semibold ${darkMode ? 'text-zinc-300' : 'text-gray-700'}`}>Total Price:</span> ${calculatePrice()}</p>
                            <p><span className={`font-semibold ${darkMode ? 'text-zinc-300' : 'text-gray-700'}`}>Subjects:</span> {Array.from(new Set(bookedSessions.map(s => s.subject))).join(', ')}</p>
                          </div>
                        </div>
                      </div>
                      
                      {/* Session Breakdown */}
                      {bookedSessions.length > 0 && (
                        <div className="mt-8 pt-8 border-t-2 border-dashed border-purple-300/60 dark:border-purple-700/40">
                          <h4 className={`font-bold text-xl mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                            Sessions Breakdown
                          </h4>
                          <div className="space-y-3">
                            {bookedSessions.map((session, index) => (
                              <div key={session.key} className={`p-4 rounded-xl border-2 ${darkMode ? 'border-purple-500/30 bg-zinc-800/60' : 'border-purple-200 bg-white'}`}>
                                <div className="flex items-center justify-between">
                                  <div>
                                    <p className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                                      Session {index + 1}: {session.subject} with {session.tutorName}
                                    </p>
                                    <p className={`text-sm ${darkMode ? 'text-zinc-400' : 'text-gray-600'}`}>
                                      {new Date(session.date + 'T00:00:00').toLocaleDateString('en-US', { 
                                        month: 'short', 
                                        day: 'numeric',
                                        year: 'numeric'
                                      })} at {formatTimeForDisplay(session.time)}
                                    </p>
                                  </div>
                                  <span className={`font-bold ${darkMode ? 'text-green-400' : 'text-green-600'}`}>
                                    $65
                                  </span>
                                </div>
                              </div>
                            ))}
                            
                            {/* Discount Note */}
                            {bookedSessions.length >= 3 && (
                              <div className={`mt-4 p-4 rounded-xl border-2 ${darkMode ? 'border-green-500/30 bg-green-900/20' : 'border-green-200 bg-green-50'}`}>
                                <div className="flex items-center justify-between">
                                  <div>
                                    <p className={`font-bold ${darkMode ? 'text-green-300' : 'text-green-700'}`}>
                                      Bulk Discount Applied!
                                    </p>
                                    <p className={`text-sm ${darkMode ? 'text-green-400' : 'text-green-600'}`}>
                                      {bookedSessions.length} sessions booked - You save ${(bookedSessions.length * 65) - calculatePrice()}
                                    </p>
                                  </div>
                                  <div className={`text-right`}>
                                    <p className={`text-sm ${darkMode ? 'text-zinc-400' : 'text-gray-600'}`}>Regular price:</p>
                                    <p className={`line-through ${darkMode ? 'text-red-400' : 'text-red-500'}`}>
                                      ${bookedSessions.length * 65}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            )}
                            
                            {/* Total Price */}
                            <div className={`mt-4 p-4 rounded-xl border-2 ${darkMode ? 'border-blue-500/30 bg-blue-900/20' : 'border-blue-200 bg-blue-50'}`}>
                              <div className="flex items-center justify-between">
                                <span className={`font-bold text-lg ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                                  Total Price:
                                </span>
                                <span className={`font-bold text-2xl ${darkMode ? 'text-red-400' : 'text-blue-600'}`}>
                                  ${calculatePrice()}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {/* Zelle Payment */}
                      <div className="mt-8 pt-8 border-t-2 border-dashed border-purple-300/60 dark:border-purple-700/40">
                        <h4 className={`font-bold text-xl mb-6 flex items-center gap-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                          <span className="
                            w-10 h-10 rounded-xl flex items-center justify-center
                            bg-gradient-to-br from-purple-500 to-pink-500 text-white
                            shadow-lg
                          ">
                            $
                          </span>
                          Payment via Zelle
                        </h4>

                        <div className="grid md:grid-cols-2 gap-6">
                          {/* QR Code */}
                          <div className="
                            flex flex-col items-center justify-center
                            rounded-2xl p-6
                            bg-white/10 border border-white/20
                            dark:bg-zinc-800/60 dark:border-purple-500/20
                            backdrop-blur-sm
                          ">
                            <img
                              src="/zelle-qr.png"
                              alt="Zelle QR Code"
                              className="w-44 h-44 rounded-xl shadow-xl mb-4"
                            />
                            <p className={`text-sm ${darkMode ? 'text-zinc-400' : 'text-gray-600'}`}>
                              Scan to pay ${calculatePrice()}
                            </p>
                          </div>

                          {/* Payment Note Info */}
                          <div className="space-y-4">
                            <div className={`p-5 rounded-xl ${darkMode ? 'bg-emerald-900/20 border-emerald-700/40' : 'bg-emerald-50 border-emerald-200'} border-2`}>
                              <h5 className={`font-bold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                                When paying, include in the note:
                              </h5>
                              <div className="space-y-2">
                                <div className="flex items-center gap-2">
                                  <div className={`w-6 h-6 rounded-full flex items-center justify-center ${darkMode ? 'bg-emerald-700 text-emerald-200' : 'bg-emerald-100 text-emerald-700'}`}>
                                    <span className="text-sm font-bold">1</span>
                                  </div>
                                  <span className={`${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                    <strong>Student:</strong> {formData.studentFirstName} {formData.studentLastName}
                                  </span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <div className={`w-6 h-6 rounded-full flex items-center justify-center ${darkMode ? 'bg-emerald-700 text-emerald-200' : 'bg-emerald-100 text-emerald-700'}`}>
                                    <span className="text-sm font-bold">2</span>
                                  </div>
                                  <span className={`${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                    <strong>Sessions:</strong> {bookedSessions.length} session{bookedSessions.length !== 1 ? 's' : ''}
                                  </span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <div className={`w-6 h-6 rounded-full flex items-center justify-center ${darkMode ? 'bg-emerald-700 text-emerald-200' : 'bg-emerald-100 text-emerald-700'}`}>
                                    <span className="text-sm font-bold">3</span>
                                  </div>
                                  <span className={`${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                    <strong>Tutors:</strong> {bookedSessions.length > 0 
                                      ? Array.from(new Set(bookedSessions.map(s => s.tutorName))).join(', ')
                                      : "None"
                                    }
                                  </span>
                                </div>
                              </div>
                            </div>

                            <div className={`p-4 rounded-xl ${darkMode ? 'bg-blue-900/20 border-blue-700/40' : 'bg-blue-50 border-blue-200'} border-2`}>
                              <p className={`text-sm ${darkMode ? 'text-blue-300' : 'text-blue-700'}`}>
                                📍 Payment secures your booking. Scan the QR code above with your bank's Zelle feature to pay ${calculatePrice()}.
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Submit Warning */}
                    <div className={`p-6 rounded-2xl border-2 ${darkMode ? 'border-yellow-500/30 bg-yellow-900/20' : 'border-yellow-200 bg-yellow-50'}`}>
                      <div className="flex items-start gap-4">
                        <Info className={`w-6 h-6 mt-1 ${darkMode ? 'text-yellow-400' : 'text-yellow-600'}`} />
                        <div>
                          <h4 className={`font-bold ${darkMode ? 'text-yellow-300' : 'text-yellow-800'} mb-2`}>Before You Submit</h4>
                          <ul className={`text-sm ${darkMode ? 'text-yellow-400' : 'text-yellow-700'} space-y-1`}>
                            <li>• <strong>Study materials are required:</strong> Please upload at least one file (notes, syllabus, textbook, etc.)</li>
                            <li>• <strong>Learning goals are required:</strong> Please provide detailed learning goals for better tutoring</li>
                            <li>• Review all information carefully before submitting</li>
                            <li>• Make sure your contact information is correct</li>
                            <li>• Verify your selected time slots</li>
                            <li>• Once submitted, you'll receive a confirmation email within 24 hours</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Navigation Buttons */}
              <div className={`flex justify-between pt-10 border-t-2 ${darkMode ? 'border-purple-500/30' : 'border-purple-200'}`}>
                <button
                  type="button"
                  onClick={prevStep}
                  disabled={currentStep === 1}
                  className={`flex items-center gap-3 px-8 py-4 rounded-xl font-bold transition-all duration-300 ${currentStep === 1
                    ? 'bg-gray-200 dark:bg-zinc-800 text-gray-500 dark:text-zinc-500 cursor-not-allowed'
                    : `${darkMode ? 'bg-gradient-to-r from-red-500 to-orange-500 hover:shadow-[0_0_25px_rgba(239,68,68,0.5)]' : 'bg-gradient-to-r from-blue-600 to-purple-600 hover:shadow-xl'} text-white hover:scale-105 shadow-lg`
                  }`}
                >
                  <ChevronLeft className="w-6 h-6" />
                  <span className="text-lg">Previous</span>
                </button>

                {currentStep < 3 ? (
                  <button
                    type="button"
                    onClick={nextStep}
                    className={`flex items-center gap-3 px-8 py-4 rounded-xl font-bold hover:scale-105 transition-all duration-300 shadow-lg ${darkMode ? 'bg-gradient-to-r from-red-500 to-orange-500 hover:shadow-[0_0_25px_rgba(239,68,68,0.5)]' : 'bg-gradient-to-r from-blue-600 to-purple-600 hover:shadow-xl'} text-white`}
                  >
                    <span className="text-lg">Next</span>
                    <ChevronRight className="w-6 h-6" />
                  </button>
                ) : (
                  <button
                    type="submit"
                    disabled={isSubmitting || loading}
                    className={`flex items-center gap-3 px-10 py-5 rounded-xl font-bold hover:scale-105 transition-all duration-300 shadow-xl ${darkMode ? 'bg-gradient-to-r from-red-500 to-orange-500 hover:shadow-[0_0_30px_rgba(239,68,68,0.6)]' : 'bg-gradient-to-r from-blue-600 to-purple-600 hover:shadow-2xl'} text-white disabled:opacity-70 disabled:cursor-not-allowed`}
                  >
                    {isSubmitting ? (
                      <>
                        <div className="w-6 h-6 border-3 border-white border-t-transparent rounded-full animate-spin"></div>
                        <span className="text-lg">Processing...</span>
                      </>
                    ) : (
                      <>
                        <span className="text-lg">Submit Booking Request</span>
                        <Send className="w-6 h-6" />
                      </>
                    )}
                  </button>
                )}
              </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes scaleIn {
          from {
            opacity: 0;
            transform: scale(0);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        @keyframes float {
          0%, 100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-20px);
          }
        }

        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }

        .animate-fadeIn {
          animation: fadeIn 0.6s ease-out;
        }

        .animate-slideUp {
          animation: slideUp 0.6s ease-out 0.2s both;
        }

        .animate-scaleIn {
          animation: scaleIn 0.5s ease-out 0.2s both;
        }

        .animate-float {
          animation: float 6s ease-in-out infinite;
        }

        .animate-spin {
          animation: spin 1s linear infinite;
        }
        
        @keyframes pulse-slow {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.7;
          }
        }

        .animate-pulse-slow {
          animation: pulse-slow 2s ease-in-out infinite;
        }

        .custom-scrollbar-thin::-webkit-scrollbar {
          width: 4px;
        }

        .custom-scrollbar-thin::-webkit-scrollbar-track {
          background: transparent;
        }

        .custom-scrollbar-thin::-webkit-scrollbar-thumb {
          background: rgba(0, 0, 0, 0.1);
          border-radius: 10px;
        }

        .custom-scrollbar-thin::-webkit-scrollbar-thumb:hover {
          background: rgba(0, 0, 0, 0.2);
        }
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }

        .custom-scrollbar::-webkit-scrollbar-track {
          background: rgba(0, 0, 0, 0.1);
          border-radius: 10px;
        }

        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(59, 130, 246, 0.5);
          border-radius: 10px;
        }

        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(59, 130, 246, 0.7);
        }
      `}</style>
    </div>
  );
}